import { makeAutoObservable } from 'mobx'
import {
	PERIOD_ACADEMIC_YEAR,
	PERIOD_CUSTOM,
	isCustomPeriod,
	isSelectableReportPeriod,
	isValidInclusiveCustomRange,
	isValidReportPeriod,
} from '../utils/reportsPeriods'

/**
 * Session-level Reports reporting-period state (applied + custom draft).
 * Applied values drive fetches; draft edits never fetch until Apply.
 */
export class ReportsPeriodStore {
	/** Applied period key used for API requests. */
	appliedType = PERIOD_ACADEMIC_YEAR
	appliedFrom = ''
	appliedTo = ''

	/** Dropdown selection (may be custom before Apply). */
	selectedType = PERIOD_ACADEMIC_YEAR

	/** Editable custom draft (UI only until Apply). */
	draftFrom = ''
	draftTo = ''

	/** Last successfully applied custom range (restored when re-opening Custom). */
	lastCustomFrom = ''
	lastCustomTo = ''

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	get isCustomSelected() {
		return isCustomPeriod(this.selectedType)
	}

	get isCustomApplied() {
		return isCustomPeriod(this.appliedType)
	}

	get isDraftValid() {
		return isValidInclusiveCustomRange(this.draftFrom, this.draftTo)
	}

	get isDraftInvalid() {
		return (
			this.isCustomSelected &&
			Boolean(this.draftFrom || this.draftTo) &&
			!this.isDraftValid
		)
	}

	get isDraftDirty() {
		if (!this.isCustomApplied) {
			return this.isDraftValid
		}
		return (
			this.draftFrom !== this.appliedFrom || this.draftTo !== this.appliedTo
		)
	}

	get canApplyCustom() {
		return this.isCustomSelected && this.isDraftValid && this.isDraftDirty
	}

	/** Params for insights fetch (applied only). */
	get appliedFetchArgs() {
		if (this.isCustomApplied) {
			return {
				period: PERIOD_CUSTOM,
				customFrom: this.appliedFrom,
				customTo: this.appliedTo,
			}
		}
		return {
			period: this.appliedType,
			customFrom: '',
			customTo: '',
		}
	}

	selectPeriod(nextType) {
		const key = String(nextType || '').trim()
		if (!isSelectableReportPeriod(key)) return

		if (isCustomPeriod(key)) {
			this.selectedType = PERIOD_CUSTOM
			this.draftFrom = this.lastCustomFrom || ''
			this.draftTo = this.lastCustomTo || ''
			return
		}

		if (!isValidReportPeriod(key)) return
		this.selectedType = key
		this.appliedType = key
		this.appliedFrom = ''
		this.appliedTo = ''
	}

	setDraftFrom(value) {
		this.draftFrom = value || ''
	}

	setDraftTo(value) {
		this.draftTo = value || ''
	}

	applyCustom() {
		if (!this.canApplyCustom) return false
		this.appliedType = PERIOD_CUSTOM
		this.appliedFrom = this.draftFrom
		this.appliedTo = this.draftTo
		this.lastCustomFrom = this.draftFrom
		this.lastCustomTo = this.draftTo
		this.selectedType = PERIOD_CUSTOM
		return true
	}

	/** Test helper / reset when leaving Reports section is not required for v1. */
	resetToDefault() {
		this.appliedType = PERIOD_ACADEMIC_YEAR
		this.appliedFrom = ''
		this.appliedTo = ''
		this.selectedType = PERIOD_ACADEMIC_YEAR
		this.draftFrom = ''
		this.draftTo = ''
		this.lastCustomFrom = ''
		this.lastCustomTo = ''
	}
}

export const reportsPeriodStore = new ReportsPeriodStore()
